/**
 * PressableScale — the one press/hover feel for tappable things: lifts a pixel
 * on hover, dips on press, springs back on release, shows a focus ring for
 * keyboard users. Web uses CSS transitions (no JS per frame); native uses an
 * Animated spring. Reduced motion keeps color feedback, drops movement.
 *
 *   <PressableScale onPress={...} style={s.chip} hoverStyle={s.chipHover} pressedStyle={s.chipDown}>
 *
 * `style`, `hoverStyle`, `pressedStyle` may be objects or arrays. `scaleTo`
 * is the pressed scale (0.97 default; use ~0.99 for large cards).
 */
import React, { useRef } from 'react';
import { Pressable, Animated } from 'react-native';
import { IS_WEB, webTransition, spring, useReducedMotion } from '../../utils/motion';

// The keyboard focus ring lives in web/custom.css ([data-pressable]:focus-visible):
// an inline outline would also show after mouse clicks (RNW focuses on mousedown).

export default function PressableScale({
    children,
    style,
    hoverStyle,
    pressedStyle,
    disabledStyle,
    scaleTo = 0.97,
    lift = 1,
    disabled = false,
    onPressIn,
    onPressOut,
    ...rest
}) {
    const reduced = useReducedMotion();
    const scale = useRef(new Animated.Value(1)).current;

    if (IS_WEB) {
        return (
            <Pressable
                disabled={disabled}
                onPressIn={onPressIn}
                onPressOut={onPressOut}
                dataSet={{ pressable: 'scale' }}
                style={({ hovered, pressed }) => [
                    webTransition(['transform', 'background-color', 'border-color', 'box-shadow', 'opacity'], pressed ? 'instant' : 'fast', pressed ? 'standard' : 'spring'),
                    { cursor: disabled ? 'default' : 'pointer', outlineStyle: 'none' },
                    style,
                    !disabled && hovered && hoverStyle,
                    !disabled && pressed && pressedStyle,
                    disabled && (disabledStyle === undefined ? { opacity: 0.45 } : disabledStyle),
                    !reduced && !disabled && pressed && { transform: [{ scale: scaleTo }] },
                    !reduced && !disabled && hovered && !pressed && lift && { transform: [{ translateY: -lift }] },
                ]}
                {...rest}
            >
                {children}
            </Pressable>
        );
    }

    return (
        <Pressable
            disabled={disabled}
            onPressIn={(e) => { if (!disabled) spring(scale, scaleTo, 'snappy').start(); onPressIn?.(e); }}
            onPressOut={(e) => { spring(scale, 1, 'pop').start(); onPressOut?.(e); }}
            {...rest}
        >
            {({ pressed }) => (
                <Animated.View style={[style, pressed && pressedStyle, disabled && (disabledStyle === undefined ? { opacity: 0.45 } : disabledStyle), { transform: [{ scale }] }]}>
                    {children}
                </Animated.View>
            )}
        </Pressable>
    );
}
